// CountDown.jsx
import React, { useState, useEffect, useRef } from 'react';
import './CountDown.css';
import { useNavigate, useLocation } from 'react-router-dom';
import en from '../locales/eng.json';
import th from '../locales/th.json';
import { QRCodeSVG } from 'qrcode.react';
import Barcode from 'react-barcode';
import CONFIG from '../config';

function CountDown() {
  const [mode, setMode] = useState('QR'); // or 'BARCODE'

  const navigate = useNavigate();
  const location = useLocation();

  // Query params
  const queryParams = new URLSearchParams(location.search);
  const couponId = queryParams.get("coupon_id");
  const userCouponId = queryParams.get("user_coupon_id");

  // Localization
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'TH');
  const [t, setT] = useState(language === 'EN' ? en : th);

  // Coupon data & countdown
  const [coupon, setCoupon] = useState(null);
  const [expiredAt, setExpiredAt] = useState(null);       // UTC timestamp (ms) when coupon expires
  const [remainingTime, setRemainingTime] = useState(0);  // seconds left
  const [adminConfirmed, setAdminConfirmed] = useState(false);

  // Loading / menu / userData
  const [isLoading, setIsLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [userData, setUserData] = useState(null);

  // Keep a ref to the polling interval so we can clear it on unmount
  const pollIntervalRef = useRef(null);

  // Fetch user data once on mount
  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(
          `${CONFIG.API_BASE_URL}/lineuser/fetch_user_data?line_user_id=` +
            localStorage.getItem('line_user_id')
        );
        const result = await res.json();

        if (result?.content?.data) {
          setUserData(result.content.data);
        } else {
          throw new Error("Invalid user data format");
        }
      } catch (e) {
        console.error("Failed to fetch user data:", e);
        navigate('/'); // Navigate away if error
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  // ---- 1. Poll coupon data every 10s to check admin_confirm & set expireTime once ----
  useEffect(() => {
    if (!userCouponId) return;

    const fetchCouponData = async () => {
      try {
        const res = await fetch(
          `${CONFIG.API_BASE_URL}/coupon/user_coupon_by_only_id?id=${userCouponId}`
        );
        const result = await res.json();
        const couponData = result?.content?.data?.[0];
        if (!couponData) return;

        // If server has marked admin_confirm = true, stop polling
        if (couponData.admin_confirm === true) {
          setAdminConfirmed(true);
          setExpiredAt(null); // no countdown needed
          return;
        }

        // Otherwise, if used_at is set, compute expireTime in UTC
        if (couponData.used_at) {
          const baseUtc = new Date(couponData.used_at).getTime();
          const expiresAt = baseUtc + 15 * 60 * 1000; // +15 minutes (UTC)
          setExpiredAt(expiresAt);
        }
      } catch (err) {
        console.error("Error fetching coupon data:", err);
      }
    };

    // Immediately fetch once
    fetchCouponData();

    // Then set up 10-second polling, if adminConfirmed still false
    pollIntervalRef.current = setInterval(() => {
      fetchCouponData();
    }, 10000);

    // Cleanup on unmount
    return () => {
      clearInterval(pollIntervalRef.current);
    };
  }, [userCouponId]);

  // ---- 2. Countdown timer (updates every second) whenever expiredAt is set and adminConfirmed is false ----
  useEffect(() => {
    if (expiredAt === null || adminConfirmed) {
      setRemainingTime(0);
      return;
    }

    // Compute initial remainingTime immediately
    const updateRemaining = () => {
      const nowUtc = Date.now();
      const diffSec = Math.max(0, Math.floor((expiredAt - nowUtc) / 1000));
      setRemainingTime(diffSec);
    };
    updateRemaining();

    // Then update every second
    const timerId = setInterval(() => {
      updateRemaining();
    }, 1000);

    return () => {
      clearInterval(timerId);
    };
  }, [expiredAt, adminConfirmed]);

  // ---- 3. Fetch coupon details for display (name, description, image, etc.) ----
  useEffect(() => {
    if (!couponId) return;

    const fetchCouponDetails = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(
          `${CONFIG.API_BASE_URL}/coupon/?coupon_id=${couponId}&page=1&limit=1000`
        );
        const result = await res.json();
        if (result?.content?.data?.length > 0) {
          setCoupon(result.content.data[0]);
        }
      } catch (err) {
        console.error("Failed to fetch coupon details:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCouponDetails();
  }, [couponId]);

  // ---- 4. Localization effect ----
  useEffect(() => {
    localStorage.setItem('language', language);
    setT(language === 'EN' ? en : th);
  }, [language]);

  // ---- 5. Copy coupon code handler ----
  const handleCopy = () => {
    const codeString = `UF${userCouponId}CID${couponId},${coupon?.discount_value}`;
    navigator.clipboard.writeText(codeString);
    alert('Copied to clipboard!');
  };

  // ---- 6. Render ----
  return (
    <div>
      {/* Full‐screen loading overlay */}
      {isLoading && (
        <div className="loading-overlay">
          <span className="loading-text">Loading...</span>
        </div>
      )}

      {/* Top bar */}
      <div className="top-section-countdown">
        <div className="top-bar">
          <img
            onClick={() => navigate('/')}
            src="/assets/logo-ujoy.png"
            alt="logo"
            className="logo-ujoy"
          />
          <div className="right-section">
            <div className="lang-selector">
              <button
                className={`lang-button ${language === 'TH' ? 'active' : ''}`}
                onClick={() => setLanguage('TH')}
              >
                TH
              </button>
              |
              <button
                className={`lang-button ${language === 'EN' ? 'active' : ''}`}
                onClick={() => setLanguage('EN')}
              >
                EN
              </button>
            </div>
            <img
              onClick={() => navigate('/profile')}
              src={
                userData?.line_image_url
                  ? userData.line_image_url
                  : "/assets/avartar.png"
              }
              alt="avatar"
              className="profile-avatar"
            />
          </div>
        </div>
      </div>

      {/* Header with back button */}
      <br />

      <div className="point-header-bar">
        <button className="back-button" onClick={() => navigate(-1)}>
          <img src="/assets/arrow_back.png" alt="Back" className="back-icon" />
        </button>
        <span className="point-header-title">
          {t.coupon_description.coupon_description}
        </span>
      </div>
      <br />

      <div className="info-summary-section-countdown">
        <div className="member-id-card-countdown">
          <img
            src={coupon?.image_url}
            alt="CouponImage"
            className="coupon-image"
          />

          <div className="countdown-box">
            {adminConfirmed ? (
              <>
                <span className="countdown-label">Countdown Timer</span>
                <span className="countdown-time">{t.countdown.used_coupon}</span>
              </>
            ) : remainingTime <= 0 ? (
              <>
                <span className="countdown-label">Countdown Timer</span>
                <span className="countdown-time expired-text">
                  {t.countdown.expired_coupon || 'Expired'}
                </span>
              </>
            ) : (
              <>
                <span className="countdown-label">Countdown Timer</span>
                <span className="countdown-time">
                  {String(Math.floor(remainingTime / 60)).padStart(2, '0')} :{' '}
                  {String(remainingTime % 60).padStart(2, '0')}
                  <span className="countdown-unit"> Mins.</span>
                </span>
              </>
            )}
          </div>
        </div>

        <p className="qr-title">{coupon?.name}</p>

        {/* QR/Barcode toggle */}
        <div className="tab-toggle-section-countdown">
          <button
            className={`tab-button ${mode === 'QR' ? 'active' : ''}`}
            onClick={() => setMode('QR')}
          >
            <div style={{ paddingLeft: '20px', paddingRight: '20px' }}>
              QR Code
            </div>
          </button>
          <button
            className={`tab-button ${mode === 'BARCODE' ? 'active' : ''}`}
            onClick={() => setMode('BARCODE')}
          >
            <div style={{ paddingLeft: '20px', paddingRight: '20px' }}>
              Barcode
            </div>
          </button>
        </div>

        <div style={{ fontWeight: 'bold', color: '#079AAE' }}>
          {t.pointcollect.show_qr}
        </div>

        {/* QR Mode */}
        {mode === 'QR' && (
          <div className="qr-mode-wrapper-countdown">
            {couponId ? (
              <div className="qr-card-countdown">
                <div className="qr-image-wrapper">
                  <QRCodeSVG
                    value={`https://ujoy-uficon.web.app/point-check?coupon_id=${couponId}&user_coupon_id=${userCouponId}&line_user_id=${localStorage.getItem(
                      'line_user_id'
                    )}`}
                    size={200}
                  />
                  <img
                    src="/assets/logo-qr.png"
                    alt="QR Logo"
                    className="qr-logo-overlay"
                  />
                </div>
              </div>
            ) : (
              <p>Loading QR...</p>
            )}
          </div>
        )}

        {/* Barcode Mode */}
        {mode === 'BARCODE' && (
          <div
            className="barcode-mode-section-countdown"
            style={{ textAlign: 'center', marginTop: 20 }}
          >
            {couponId ? (
              <div
                style={{
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '16px',
                  display: 'block',
                  minWidth: '70vw',
                  margin: '20px auto 30px',
                  boxShadow: '0px 10px 25px rgba(0, 0, 0, 0.08)',
                }}
              >
                <Barcode
                  value={`UF${userCouponId}CID${couponId},${coupon?.discount_value}`}
                  width={1}
                  height={80}
                  fontSize={16}
                />
              </div>
            ) : (
              <p>Loading Barcode...</p>
            )}
          </div>
        )}

        {/* Coupon code and copy */}
        <div className="coupon-code-box">
          <span className="coupon-label">หมายเลขคูปอง</span>
          <span className="coupon-code">
            {`UF${userCouponId}CID${couponId},${coupon?.discount_value}`}
          </span>
          <span className="coupon-copy" onClick={handleCopy}>
            copy
          </span>
        </div>
        <br />
      </div>

      {/* Coupon info (name & description) */}
      <div className="coupon-info-section-countdown">
        <h3 className="coupon-title-countdown">{coupon?.name}</h3>
        <p className="coupon-description-countdown">{coupon?.description}</p>
      </div>
    </div>
  );
}

export default CountDown;
